import { connect, ConnectedProps } from 'react-redux';
import { useNavigate } from 'react-router-dom-v5-compat';

import SessionClientDashboard from 'dashboard/lib/clients/SessionClientDashboard';
import { MessageType } from 'dashboard/lib/interfaces/Message';
import { productFeatureDisabled } from 'dashboard/lib/productFeature';
import { CaseAssignmentPolicy } from 'dashboard/models/case-assignment-policy';
import { ProductFeatureName } from 'dashboard/models/organization';
import { showMessage } from 'dashboard/redux/actions/messages';
import {
  setCurrentPage,
  setFilters,
  setPageSize,
  setSortParams,
} from 'dashboard/redux/slices/case-assignment-policies';
import { RootState } from 'dashboard/redux/store';
import { loadCaseAssignmentPoliciesList } from 'dashboard/redux/thunk-actions/case-assignment-policies';
import DashboardCaseAssignmentPoliciesView from 'dashboard/views/DashboardCaseAssignmentPoliciesView';
import { CreateCaseAssignmentPolicyFormData } from 'dashboard/views/DashboardCaseAssignmentPoliciesView/FormCreateCaseAssignmentPolicy';
import useApiCallback from 'lib/hooks/useApiCallback';
import useApiErrorHandler from 'lib/hooks/useApiErrorHandler';
import useApiLoader from 'lib/hooks/useApiLoader';
import useTable from 'lib/hooks/useTable';
import { ApiEndpoint, ApiModel } from 'lib/interfaces/api';
import { isPermissionsError, permissionsErrorMessage } from 'lib/permissions-errors';

const DashboardCaseAssignmentPoliciesRoute = (props: PropsFromRedux) => {
  const navigate = useNavigate();

  const { loading: loadingCaseAssignmentPolicies, callback: caseAssignmentPolicyLoader } =
    useApiLoader({
      loader: () =>
        props.loadCaseAssignmentPoliciesList({
          currentPage: props.currentPage,
          pageSize: props.pageSize,
          sortParams: props.sortParams,
          filters: props.filters,
        }),
      deps: [
        props.loadCaseAssignmentPoliciesList,
        props.currentPage,
        props.pageSize,
        props.sortParams,
        props.filters,
      ],
      onError: (error) => {
        if (isPermissionsError(error)) {
          props.showMessage(permissionsErrorMessage(error), MessageType.Failure);
        } else {
          switch (error.status) {
            case 401:
              props.showMessage(
                'Your role does not have permissions to view case assignment policies in this organization.',
                MessageType.Notice,
              );
              break;
            default:
              props.showMessage('Failed to load case assignment policies.', MessageType.Failure);
          }
        }
      },
      onNetworkError: () =>
        props.showMessage(
          'We are experiencing network issues. Please try again.',
          MessageType.Failure,
        ),
    });

  const { error: createCaseAssignmentPolicyError, callback: createCaseAssignmentPolicy } =
    useApiCallback(
      async ({ name, distributionType, assignmentOrderBy }: CreateCaseAssignmentPolicyFormData) => {
        const response = await SessionClientDashboard.postResourceCreate<CaseAssignmentPolicy>({
          attributes: {
            name: name,
            'distribution-type': distributionType,
            'assignment-order-by': assignmentOrderBy,
          },
          type: ApiModel.CaseAssignmentPolicy,
        });

        props.showMessage('Successfully created assignment policy.', MessageType.Success, true);
        navigate(`/dashboard/case-assignment-policies/${response.data.id}`);
      },
      [],
    );

  useApiErrorHandler({
    error: createCaseAssignmentPolicyError,
    onError: (error) => {
      if (isPermissionsError(error)) {
        props.showMessage(permissionsErrorMessage(error), MessageType.Failure);
      } else {
        props.showMessage(
          'Failed to create assignment policy, please try again.',
          MessageType.Failure,
          false,
        );
      }
    },
    onNetworkError: () =>
      props.showMessage(
        'We are experiencing network issues. Please try again.',
        MessageType.Failure,
      ),
  });

  const { error: archiveCaseAssignmentPolicyError, callback: archiveCaseAssignmentPolicy } =
    useApiCallback(
      async (caseAssignmentPolicy: CaseAssignmentPolicy) => {
        await SessionClientDashboard.deleteResource<CaseAssignmentPolicy>(
          ApiEndpoint.CaseAssignmentPolicy,
          caseAssignmentPolicy.id,
        );
        caseAssignmentPolicyLoader();
        props.showMessage('Successfully archived assignment policy.', MessageType.Success);
      },
      [caseAssignmentPolicyLoader],
    );

  useApiErrorHandler({
    error: archiveCaseAssignmentPolicyError,
    onError: (error) => {
      if (isPermissionsError(error)) {
        props.showMessage(permissionsErrorMessage(error), MessageType.Failure);
      } else {
        props.showMessage(
          'Failed to archive assignment policy, please try again.',
          MessageType.Failure,
          false,
        );
      }
    },
    onNetworkError: () =>
      props.showMessage(
        'Failed to archive assignment policy, please try again.',
        MessageType.Failure,
        false,
      ),
  });

  const { handlePageLinkClick, handlePageSizeClick, handleSortUpdate } = useTable({
    filters: props.filters,
    sortParams: props.sortParams,
    pageSize: props.pageSize,
    currentPage: props.currentPage,
    setFilters: (filters) => props.setFilters({ filters }),
    setCurrentPage: (currentPage) => props.setCurrentPage({ currentPage }),
    setPageSize: (pageSize) => props.setPageSize({ pageSize }),
    setSortParams: (sortParams) => props.setSortParams({ sortParams }),
  });

  const hasCasesFree = !productFeatureDisabled(
    props.organization,
    ProductFeatureName.CasesFree,
    props.currentEnvironment.type,
  );

  const hasCasesBasic = !productFeatureDisabled(
    props.organization,
    ProductFeatureName.CasesBasic,
    props.currentEnvironment.type,
  );

  return (
    <DashboardCaseAssignmentPoliciesView
      atActiveCaseAssignmentPolicyLimit={props.atActiveCaseAssignmentPolicyLimit}
      caseAssignmentPolicies={props.caseAssignmentPolicies}
      currentEnvironment={props.currentEnvironment}
      hasCasesFree={hasCasesFree}
      hasCasesBasic={hasCasesBasic}
      loadingCaseAssignmentPolicies={loadingCaseAssignmentPolicies}
      paginationParams={props.paginationParams}
      pageSize={props.pageSize}
      sortParams={props.sortParams}
      createCaseAssignmentPolicy={createCaseAssignmentPolicy}
      archiveCaseAssignmentPolicy={archiveCaseAssignmentPolicy}
      handlePageLinkClick={handlePageLinkClick}
      handlePageSizeClick={handlePageSizeClick}
      handleSortUpdate={handleSortUpdate}
    />
  );
};

const mapStateToProps = (state: RootState) => {
  return {
    atActiveCaseAssignmentPolicyLimit:
      state.caseAssignmentPolicies.atActiveCaseAssignmentPolicyLimit,
    caseAssignmentPolicies: state.caseAssignmentPolicies.caseAssignmentPolicies,
    currentEnvironment: state.currentEnvironment.currentEnvironment,
    currentPage: state.caseAssignmentPolicies.currentPage,
    filters: state.caseAssignmentPolicies.filters,
    organization: state.organization.organization,
    pageSize: state.caseAssignmentPolicies.pageSize,
    paginationParams: state.caseAssignmentPolicies.paginationParams,
    sortParams: state.caseAssignmentPolicies.sortParams,
  };
};

const mapDispatchToProps = {
  loadCaseAssignmentPoliciesList,
  showMessage,
  setSortParams,
  setFilters,
  setCurrentPage,
  setPageSize,
};

const connector = connect(mapStateToProps, mapDispatchToProps);
type PropsFromRedux = ConnectedProps<typeof connector>;

export default connector(DashboardCaseAssignmentPoliciesRoute);
